/** Run CRUD retains store projection ownership; command custody is separate. */
import { useCallback, useRef } from 'react';
import { createOfflineDraftRun, createRun, deleteRun, discardCachedRunProposal,
  discardOfflineDraftRun, isOfflineDraftRun, renameCachedRunProposal,
  renameOfflineDraftRun, renameRun } from '@/api/runs';

const useRunCRUD = ({ commands, selectedDateString, setRuns, setSelectedRunId,
  captureSelection, isSelectionCurrent, getRun, offline = false, runCacheLastUpdated = null }) => {
  const renames = useRef(new Map());
  const handleCreateRun = useCallback(async name => {
    const trimmed = name.trim();
    if (!trimmed || !selectedDateString) return false;
    const selection = captureSelection();
    const result = await commands.execute({ kind: 'create', name: trimmed }, {
      send: config => offline ? createOfflineDraftRun(trimmed, selectedDateString, config) : createRun(trimmed, selectedDateString, config),
      message: offline ? 'Offline draft run created' : 'Run created', refresh: false,
      onAccepted: created => {
        const newRun = { ...created, status: 'draft' };
        setRuns(previous => [...previous.filter(run => run.id !== newRun.id), newRun]);
        if (isSelectionCurrent(selection)) setSelectedRunId(newRun.id);
      },
    });
    return result.accepted;
  }, [commands, selectedDateString, offline, captureSelection, setRuns, isSelectionCurrent, setSelectedRunId]);

  const handleRenameRun = useCallback(async (runId, name, runPlan) => {
    const trimmed = name.trim();
    const run = getRun(runId);
    if (!trimmed || !run || commands.public.blockedRun(runId)) return false;
    if (run.name === trimmed) return true;
    const previousName = run.name;
    const edit = {};
    let optimisticRun;
    const result = await commands.execute({ kind: 'rename', runId, expectedName: trimmed, ...(runPlan ? { runPlan } : {}) }, {
      onStart: () => {
        renames.current.set(runId, edit);
        setRuns(previous => previous.map(row => row.id === runId ? { ...row, name: trimmed } : row));
        optimisticRun = getRun(runId);
      },
      send: config => !offline ? renameRun(runId, trimmed, config)
        : isOfflineDraftRun(run) ? renameOfflineDraftRun(runId, trimmed, config)
          : renameCachedRunProposal(runId, trimmed, runCacheLastUpdated, config),
      message: 'Run renamed', refresh: false,
      onAccepted: data => {
        if (renames.current.get(runId) === edit) {
          // A newly accepted write fences reads started during persistence.
          setRuns(previous => previous.map(row => row.id === runId ? { ...row, ...(offline ? data : { name: trimmed }) } : row));
        }
      },
      onRefused: () => {
        if (renames.current.get(runId) === edit && getRun(runId) === optimisticRun) {
          setRuns(previous => previous.map(row => row.id === runId ? { ...row, name: previousName } : row));
        }
        return false;
      },
    });
    if (renames.current.get(runId) === edit) renames.current.delete(runId);
    return result.accepted;
  }, [commands, getRun, setRuns, offline, runCacheLastUpdated]);

  const handleDiscardCachedRunProposal = useCallback(async (runId, runPlan) => {
    if (!offline || isOfflineDraftRun(getRun(runId))) return false;
    const result = await commands.execute({ kind: 'discard', runId, source: 'cached', ...(runPlan ? { runPlan } : {}) }, {
      send: config => discardCachedRunProposal(runId, config),
      message: 'Offline changes discarded', refresh: false,
      onAccepted: restored => setRuns(previous => previous.map(run => run.id === runId ? restored : run)),
    });
    return result.accepted;
  }, [commands, offline, getRun, setRuns]);

  const handleDeleteRun = useCallback(async (runId, runPlan) => {
    if (!getRun(runId) || (offline && !isOfflineDraftRun(getRun(runId)))) return false;
    const result = await commands.execute({ kind: 'delete', runId, ...(runPlan ? { runPlan } : {}) }, {
      send: config => offline ? discardOfflineDraftRun(runId, config) : deleteRun(runId, config),
      message: offline ? 'Offline draft run discarded' : 'Run deleted', refresh: false, orders: true,
      onAccepted: () => {
        setRuns(previous => previous.filter(run => run.id !== runId));
        setSelectedRunId(current => current === runId ? null : current);
      },
    });
    return result.accepted;
  }, [commands, getRun, offline, setRuns, setSelectedRunId]);
  return { handleCreateRun, handleRenameRun, handleDeleteRun, handleDiscardCachedRunProposal };
};
export default useRunCRUD;
